import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type SavedOpportunitiesWidgetProps,
  type SavedOpportunitiesThemingProps,
  SAVED_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionSavedOpportunities for imperative control.
 */
export interface LegionSavedOpportunitiesHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildSavedOpportunitiesWidgetUrl(
  props: SavedOpportunitiesWidgetProps
): string {
  const { baseUrl, theme = "light" } = props;

  const url = new URL("/widget/saved-opportunities", baseUrl);
  url.searchParams.set("theme", theme);

  // Append theming CSS variable params
  const themingKeys = Object.keys(SAVED_THEMING_PROP_TO_PARAM) as Array<
    keyof SavedOpportunitiesThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(SAVED_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionSavedOpportunities – a React component that embeds the Legion
 * Saved Opportunities widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionSavedOpportunities } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionSavedOpportunities
 *       baseUrl="https://your-legion-domain.com"
 *       authToken="your-jwt-token"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionSavedOpportunities = forwardRef<
  LegionSavedOpportunitiesHandle,
  SavedOpportunitiesWidgetProps
>(function LegionSavedOpportunities(props, ref) {
  const {
    theme = "light",
    authToken,
    width = "100%",
    height = "400px",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildSavedOpportunitiesWidgetUrl(props),
    [
      baseUrl,
      theme,
      ...Object.keys(SAVED_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    // Send auth token via postMessage
    const iframe = iframeRef.current;
    if (authToken && iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "AUTH_TOKEN", token: authToken },
        baseUrl
      );
    }
    onLoad?.({ widget: "saved-opportunities", theme: theme ?? "light" });
  }, [onLoad, theme, authToken, baseUrl]);

  const handleError = useCallback(() => {
    onError?.("Failed to load saved opportunities widget");
  }, [onError]);

  return (
    <div
      className={className}
      style={{ width, height, ...style }}
      data-testid="legion-saved-opportunities"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Saved Opportunities Widget"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        style={{ border: "none" }}
      />
    </div>
  );
});

